// Названия разделов каталога (табы витрины, табы корзины).
// Источник — админ-настройки (GET /api/settings → catalog_label_*),
// переименовываются владельцем без пересборки. До ответа API — названия с прошлого открытия
// (localStorage), а при первом открытии вкладки ждут ответа; при ошибке — дефолты.
// Раньше до ответа показывались дефолты: у Стемса после долгого перерыва в боте сначала
// мелькало «Букеты», потом «Сеты» (жалоба клиента, 28.10).
import { useEffect, useState } from 'react';
import { fetchAppSettings, FOREVER } from './settings';

const fallback = {
  products: 'Стебли',
  bouquets: 'Букеты',
  subscription: 'Подписка',
};

let cache = null;   // производное значение; ставится только при успехе, неудача не кэшируется

const STORE_KEY = 'nf_catalog_labels';
function readStored() {
  try {
    const v = JSON.parse(localStorage.getItem(STORE_KEY) || 'null');
    return v && v.products && v.bouquets && v.subscription ? v : null;
  } catch (e) {
    return null;
  }
}
const stored = readStored();

function fetchCatalogLabels() {
  if (cache) return Promise.resolve(cache);
  // Свежесть не нужна: названия разделов меняют раз в полгода. Параллельные вызовы
  // разделяют один запрос — дедупликация в settings.js.
  return fetchAppSettings({ maxAgeMs: FOREVER })
    .then((s) => {
      cache = {
        products: s.catalog_label_products || fallback.products,
        bouquets: s.catalog_label_bouquets || fallback.bouquets,
        subscription: s.catalog_label_subscription || fallback.subscription,
      };
      try { localStorage.setItem(STORE_KEY, JSON.stringify(cache)); } catch (e) { /* no-op */ }
      return cache;
    })
    // Копия, а не сам fallback: «ещё ждём» отличаем от «не загрузилось» (labelsPending)
    .catch(() => stored || { ...fallback });
}

export function useCatalogLabels() {
  const [labels, setLabels] = useState(cache || stored || fallback);
  useEffect(() => {
    let alive = true;
    fetchCatalogLabels().then((l) => { if (alive) setLabels(l); });
    return () => { alive = false; };
  }, []);
  return labels;
}

// Названия ещё не известны (первое открытие, ответа нет) — вкладки прячут текст, чтобы не мелькал дефолт
export function labelsPending(labels) {
  return labels === fallback;
}

// Родительный падеж мн. числа для текстов вида «Сетов пока нет».
// Автоматическое склонение произвольного русского слова ненадёжно
// (сеты→сетов, но розы→роз), поэтому словарь типовых названий разделов;
// неизвестное слово → null, вызывающий код показывает нейтральный текст.
const GENITIVE_PLURAL = {
  'букеты': 'букетов',
  'сеты': 'сетов',
  'наборы': 'наборов',
  'боксы': 'боксов',
  'комплекты': 'комплектов',
  'композиции': 'композиций',
  'охапки': 'охапок',
  'корзины': 'корзин',
  'моно-букеты': 'моно-букетов',
  'монобукеты': 'монобукетов',
  'стебли': 'стеблей',
  'цветы': 'цветов',
};

export function genitivePlural(label) {
  const src = (label || '').trim();
  const form = GENITIVE_PLURAL[src.toLowerCase()];
  if (!form) return null;
  // Наследуем регистр первой буквы исходного названия
  return src[0] === src[0].toUpperCase()
    ? form[0].toUpperCase() + form.slice(1)
    : form;
}

// Единственное число, нижний регистр — для бейджа позиции корзины («букет», «сет»).
// Неизвестное слово → само название в нижнем регистре (бейдж «сеты» тоже читается).
const SINGULAR = {
  'букеты': 'букет',
  'сеты': 'сет',
  'наборы': 'набор',
  'боксы': 'бокс',
  'комплекты': 'комплект',
  'композиции': 'композиция',
  'охапки': 'охапка',
  'корзины': 'корзина',
  'моно-букеты': 'моно-букет',
  'монобукеты': 'монобукет',
  'стебли': 'стебель',
  'цветы': 'цветок',
};

export function singularLower(label) {
  const low = (label || '').trim().toLowerCase();
  return SINGULAR[low] || low;
}

// Родительный падеж ед. числа, нижний регистр («Сумма заказа букета/сета»).
// Неизвестное слово → null, вызывающий код строит фразу без склонения.
const GENITIVE_SINGULAR = {
  'букеты': 'букета',
  'сеты': 'сета',
  'наборы': 'набора',
  'боксы': 'бокса',
  'комплекты': 'комплекта',
  'композиции': 'композиции',
  'охапки': 'охапки',
  'корзины': 'корзины',
  'моно-букеты': 'моно-букета',
  'монобукеты': 'монобукета',
  'стебли': 'стебля',
  'цветы': 'цветка',
};

export function genitiveSingularLower(label) {
  const low = (label || '').trim().toLowerCase();
  return GENITIVE_SINGULAR[low] || null;
}
